import React, { useMemo, useState } from 'react';
import { Layers, Rocket, ArrowRight, Search, Github } from 'lucide-react';
import { useApps } from '../hooks/useApps';
import AppGrid from '../components/apps/AppGrid';
import EditModeGate from '../components/admin/EditModeGate';
import AppFilterBar from '../components/apps/AppFilterBar';
import EmptyState from '../components/ui/EmptyState';
import { SITE, GITHUB_URL } from '../config/site';

const matchesSearch = (app, term) => {
    if (!term) return true;
    const needle = term.toLowerCase();
    return [app.title, app.tagline, app.description, app.category, ...app.tags, ...app.techStack]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(needle));
};

const Home = () => {
    const { apps, categories, loading, upsertApp } = useApps();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    const filteredApps = useMemo(
        () =>
            apps.filter(
                (app) =>
                    matchesSearch(app, searchTerm) &&
                    (selectedCategory === 'all' || app.category === selectedCategory),
            ),
        [apps, searchTerm, selectedCategory],
    );


    return (
        <div className="bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
            {/* Hero cố ý ngắn: mục đích của trang là danh sách app, không phải cái hero.
                Giữ ~60vh để hàng card đầu tiên đã ló ra khi vừa mở trang. */}
            <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute top-1/4 -left-20 w-72 h-72 md:w-96 md:h-96 bg-primary-300/20 dark:bg-primary-600/10 rounded-full blur-3xl animate-blob" />
                    <div className="absolute top-1/3 -right-20 w-72 h-72 md:w-96 md:h-96 bg-slate-400/20 dark:bg-slate-500/15 rounded-full blur-3xl animate-blob-reverse animation-delay-2000" />
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.015)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />
                </div>

                <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-16">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/60 dark:bg-white/10 backdrop-blur-glass border border-white/40 dark:border-white/10 shadow-lg shadow-slate-900/10 animate-float-gentle mb-7">
                        <img src={SITE.logo} alt={SITE.shortName} className="w-14 h-14 dark:invert" />
                    </div>

                    <h1 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-5">
                        {SITE.name}
                    </h1>

                    <p className="text-lg md:text-xl text-slate-500 dark:text-slate-400 mb-8 max-w-2xl mx-auto leading-relaxed text-balance">
                        {SITE.tagline}
                    </p>

                    {/* Một dòng gọn thay cho ba thẻ số liệu — con số ở đây là ngữ cảnh, không phải thành tích */}
                    <p className="text-sm text-slate-400 dark:text-slate-500 mb-9">
                        {apps.length} ứng dụng · {categories.length - 1} nhóm ·
                        {' '}từ {SITE.startYear} đến nay
                    </p>

                    <div className="flex flex-wrap justify-center gap-3">
                        <a href="#apps" className="btn-accent group">
                            <Rocket className="w-5 h-5" />
                            Xem tất cả ứng dụng
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </a>
                        <a
                            href={GITHUB_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-surface"
                        >
                            <Github className="w-5 h-5" />
                            GitHub
                        </a>
                    </div>
                </div>
            </section>

            <section id="apps" className="relative py-24 px-6 scroll-mt-20">
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    <div className="absolute top-20 right-0 w-64 h-64 bg-slate-300/15 dark:bg-slate-600/10 rounded-full blur-3xl animate-blob animation-delay-2000" />
                    <div className="absolute bottom-20 left-0 w-64 h-64 bg-slate-400/15 dark:bg-slate-500/10 rounded-full blur-3xl animate-blob-reverse animation-delay-4000" />
                </div>

                <div className="relative max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/50 dark:bg-white/[0.06] backdrop-blur-sm border border-white/40 dark:border-white/10 rounded-full mb-6 shadow-sm">
                            <Layers className={`w-4 h-4 text-slate-500 dark:text-slate-400 ${loading ? 'animate-pulse' : ''}`} />
                            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                                {filteredApps.length} ứng dụng
                            </span>
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                            Tất cả ứng dụng
                        </h2>
                        <p className="text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
                            Mỗi thứ ở đây bắt đầu từ một vấn đề tôi tự gặp phải. Bấm vào để xem nó
                            làm được gì trước khi mở.
                        </p>
                    </div>

                    <AppFilterBar
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        categories={categories}
                        selectedCategory={selectedCategory}
                        onCategoryChange={setSelectedCategory}
                    />

                    {/* Vẽ ngay bằng dữ liệu seed có sẵn trong state, không chờ Firebase.
                        Danh sách từ Firebase về tới đâu thì thay tới đó. */}
                    {/* Với admin đã đăng nhập, mỗi thẻ mọc thêm nút sửa; với khách
                        thì đây chỉ là một lớp bọc rỗng, không tải thêm gì. */}
                    <EditModeGate onSaved={upsertApp}>
                        {filteredApps.length > 0 && <AppGrid apps={filteredApps} />}

                        {filteredApps.length === 0 && (
                            <EmptyState
                                icon={Search}
                                title="Không có ứng dụng nào khớp"
                                message="Thử một từ khoá ngắn hơn, hoặc bỏ bộ lọc danh mục."
                            />
                        )}
                    </EditModeGate>
                </div>
            </section>
        </div>
    );
};

export default Home;
